import { useEffect } from 'react';
import { usePromptsStore, getStoredSidebarCollapsed, setStoredSidebarCollapsed } from '../store/promptsStore';
import { userPreferenceService } from '../services/firestore/userPreferenceService';
import { useAuth } from '../features/auth/hooks/useAuth';

export const usePersistedSidebar = (): void => {
  const { user } = useAuth();
  const sidebarCollapsed = usePromptsStore((state) => state.sidebarCollapsed);
  const isSidebarInitialized = usePromptsStore((state) => state.isSidebarInitialized);
  const setSidebarCollapsed = usePromptsStore((state) => state.setSidebarCollapsed);
  const setSidebarInitialized = usePromptsStore((state) => state.setSidebarInitialized);

  // Load sidebar state from Firestore once per session and sync localStorage
  useEffect(() => {
    if (!user?.uid || isSidebarInitialized) return;

    const loadPreferences = async () => {
      try {
        const prefs = await userPreferenceService.getPreferences(user.uid);
        const firestoreCollapsed = prefs?.preferences?.sidebarCollapsed;

        if (firestoreCollapsed !== undefined) {
          const localCollapsed = getStoredSidebarCollapsed();
          if (firestoreCollapsed !== localCollapsed) {
            setStoredSidebarCollapsed(firestoreCollapsed);
            setSidebarCollapsed(firestoreCollapsed);
          }
        }
      } catch (err) {
        console.error('Failed to load sidebar preferences:', err);
      } finally {
        setSidebarInitialized(true);
      }
    };

    loadPreferences();
  }, [user?.uid, isSidebarInitialized, setSidebarCollapsed, setSidebarInitialized]);

  // Persist sidebar state changes to Firestore
  useEffect(() => {
    if (!user?.uid || !isSidebarInitialized) return;

    const savePreferences = async () => {
      try {
        await userPreferenceService.setSidebarCollapsed(user.uid, sidebarCollapsed);
      } catch (err) {
        console.error('Failed to save sidebar preferences:', err);
      }
    };

    savePreferences();
  }, [user?.uid, sidebarCollapsed, isSidebarInitialized]);
};
